// Imports
import ___CSS_LOADER_API_SOURCEMAP_IMPORT___ from "../../../node_modules/css-loader/dist/runtime/sourceMaps.js";
import ___CSS_LOADER_API_IMPORT___ from "../../../node_modules/css-loader/dist/runtime/api.js";
var ___CSS_LOADER_EXPORT___ = ___CSS_LOADER_API_IMPORT___(___CSS_LOADER_API_SOURCEMAP_IMPORT___);
// Module
___CSS_LOADER_EXPORT___.push([module.id, `/*
 * Mario-style level — decorative idle animation on the footer's top line,
 * horizontally clamped (by the director) to the calendar's left/right edges.
 *
 * A capped runner sprints across a scrolling silhouette "level": it leaps a
 * pit, head-bumps a row of brick/question blocks (bursting bricks into shards),
 * stomps a mushroom-footed walker flat, then runs off the right edge.
 *
 * Layout mirrors the toy-parade strip: the stage is anchored just above the
 * footer's top border (bottom: 100%) and clips its contents. The \`left\`/\`width\`
 * are set inline by the director to the calendar's box. The world layer scrolls
 * via \`transform: translateX\` while the runner stays put and pumps its legs;
 * the two transforms live on separate elements so they never fight. Everything
 * is a flat currentColor silhouette.
 */

.idle-stage--footer-below-calendar {
  position: absolute;
  bottom: 100%;
  height: 92px;
  overflow: hidden;
  pointer-events: none;
  z-index: 0;
  color: rgb(120, 145, 175);
}

/* Shared: opaque fill + one group-level alpha so overlapping silhouette shapes
   flatten to a single solid before transparency (no dark seams). */
.idle-stage--footer-below-calendar .mario-actor__body,
.idle-stage--footer-below-calendar .mario-runner__body {
  width: 100%;
  height: 100%;
  transform-origin: 50% 100%;
}

.idle-stage--footer-below-calendar svg {
  display: block;
  width: 100%;
  height: 100%;
}

/* --- scrolling world ------------------------------------------------------ */
/* Carries all the scenery. Starts shifted right and translates fully left so
   the level slides past the fixed runner over the crossing timeline. */
.mario-world {
  position: absolute;
  left: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  opacity: 0.32;
  will-change: transform;
  /* The freeze offset is the world translate that centers the first brick over
     the fixed runner (runner 30% - brickA center 46% = -16%). The scroll is
     phased by JS-applied classes; before any class is added the world holds at
     its start offset. */
  --mario-freeze-x: -16%;
  transform: translateX(60%);
}

/* Phase A: scroll in from the start offset until the first brick sits over the
   runner, then hold (fill:both) so the world stays frozen during the jump. */
.mario-world--scroll-in {
  animation: mario-world-in var(--mario-in-ms, 5200ms) ease-out both;
}
@keyframes mario-world-in {
  0% { transform: translateX(60%); }
  100% { transform: translateX(var(--mario-freeze-x)); }
}

/* Phase B: resume from the frozen offset and scroll the rest of the level past
   (walker stomp + castle finish). Ends at the offset that places the castle's
   door directly over the fixed runner (runner 30% - castle door 108% = -78%),
   so the level halts on the castle rather than scrolling off. */
.mario-world--scroll-rest {
  animation: mario-world-rest var(--mario-rest-ms, 6000ms) linear both;
}
@keyframes mario-world-rest {
  0% { transform: translateX(var(--mario-freeze-x)); }
  100% { transform: translateX(-97%); }
}

/* Whole-stage fade-out once Mario reaches the castle door. */
.idle-stage--footer-below-calendar.mario-stage--finish {
  animation: mario-stage-fade 900ms ease-in both;
}
@keyframes mario-stage-fade {
  0% { opacity: 1; }
  100% { opacity: 0; }
}

/* Generic positioned actor inside the world (position set inline as a %). */
.mario-scenery,
.mario-pipe,
.mario-walker,
.mario-pit,
.mario-blocks {
  position: absolute;
  bottom: 0;
}

/* Background parallax: hills sit on the ground and drift a touch slower via a
   gentle counter-scroll; clouds float higher up. Runs for the whole sequence
   (scroll-in + pause + scroll-rest); JS pauses it during the jump so the
   background truly stops while Mario breaks the brick. */
.mario-scenery--hill {
  width: 90px;
  height: 40px;
  opacity: 0.6;
  animation: mario-parallax var(--mario-total-ms, 12000ms) linear both;
}

.mario-scenery--cloud {
  width: 60px;
  height: 26px;
  bottom: 56px;
  opacity: 0.5;
  animation: mario-parallax var(--mario-total-ms, 12000ms) linear both;
}

/* Slight counter-drift so background layers lag the foreground scroll. */
@keyframes mario-parallax {
  0% { transform: translateX(0); }
  100% { transform: translateX(28%); }
}

.mario-pipe {
  width: 30px;
  height: 36px;
}
.mario-pipe--tall {
  height: 48px;
}
/* The pipe SVG uses preserveAspectRatio="none" so it stretches to fill its box
   at any height, keeping the base flush on the ground line. */

/* The pit: a dark notch in the ground line the runner jumps over. */
.mario-pit {
  width: 46px;
  height: 8px;
  background: currentColor;
  border-radius: 2px;
  opacity: 0.5;
  /* Render as a "gap": a shadowed dip below the baseline. */
  bottom: -4px;
}

/* Floating block row, raised so the runner's jump apex bumps its underside. */
.mario-blocks {
  display: flex;
  gap: 3px;
  bottom: 48px;
}

.mario-block {
  position: relative;
  width: 22px;
  height: 22px;
}
.mario-block .mario-actor__body {
  width: 100%;
  height: 100%;
}

/* One-shot bounce triggered by JS on the brick actually hit (the first brick),
   at the moment of the jump apex. Runs once then settles. */
.mario-block--bumped {
  animation: mario-block-bump 220ms ease-out both;
}
@keyframes mario-block-bump {
  0% { transform: translateY(0); }
  45% { transform: translateY(-6px); }
  100% { transform: translateY(0); }
}

/* A broken brick vanishes (its shards carry the visible burst). */
.mario-block--broken {
  visibility: hidden;
}

/* --- brick shards --------------------------------------------------------- */
/* The world layer is already ~0.32 opacity, so shards need to be near-opaque
   (and a bit larger) to read against it during the quick burst. They launch up
   and outward like an explosion, then arc back down past the origin. */
.mario-shard {
  position: absolute;
  width: 14px;
  height: 14px;
  opacity: 0.95;
  animation: mario-shard-fly 680ms ease-out forwards;
}
@keyframes mario-shard-fly {
  0% { transform: translate(0, 0) rotate(0deg); opacity: 0.95; }
  40% {
    transform: translate(calc(var(--mario-shard-dx, 16px) * 0.6), var(--mario-shard-dy, -28px)) rotate(140deg);
    opacity: 0.95;
  }
  100% {
    transform: translate(var(--mario-shard-dx, 16px), 30px) rotate(300deg);
    opacity: 0;
  }
}

/* --- stompable walker ----------------------------------------------------- */
.mario-walker {
  width: 26px;
  height: 22px;
}
.mario-walker .mario-actor__body {
  animation: mario-walker-toddle 0.5s ease-in-out infinite;
}
@keyframes mario-walker-toddle {
  0%, 100% { transform: translateY(0) rotate(-2deg); }
  50% { transform: translateY(-1px) rotate(2deg); }
}
/* Flattened after the stomp: squash to the ground and stop toddling. */
.mario-walker--stomped {
  height: 8px;
}
.mario-walker--stomped .mario-actor__body {
  animation: none;
}

/* --- level-end flagpole + castle ------------------------------------------ */
.mario-flag,
.mario-castle {
  position: absolute;
  bottom: 0;
}
/* Tall flagpole with a pennant near the top. */
.mario-flag {
  width: 24px;
  height: 90px;
}
/* The castle silhouette Mario ends the level at. */
.mario-castle {
  width: 116px;
  height: 80px;
}

/* --- the runner ----------------------------------------------------------- */
/* Fixed at 30% of the stage the whole time; the world scrolls beneath it. The
   vertical jump and stomp-dip are standalone one-shot animations applied by JS
   at the right phase, so they never fight the (now phased) world scroll. The
   leg-frame swap lives on the inner layers. */
.mario-runner {
  position: absolute;
  bottom: 0;
  left: 30%;
  width: 30px;
  height: 40px;
  opacity: 0.4;
  will-change: transform;
  transform: translateY(0);
}
.mario-runner__body {
  position: relative;
  width: 100%;
  height: 100%;
}

/* Standalone straight-up jump (no horizontal motion) run once during the pause
   so Mario breaks the brick directly above him, then lands. */
.mario-runner--jump {
  animation: mario-jump 620ms cubic-bezier(0.3, 0, 0.4, 1) both;
}
@keyframes mario-jump {
  0% { transform: translateY(0); }
  50% { transform: translateY(-30px); }
  100% { transform: translateY(0); }
}

/* Small stomp dip run once when the walker aligns, during the resumed scroll. */
.mario-runner--stomp {
  animation: mario-stomp 300ms ease-in-out both;
}
@keyframes mario-stomp {
  0% { transform: translateY(0); }
  50% { transform: translateY(-18px); }
  100% { transform: translateY(0); }
}

/* The static torso and both leg frames are stacked on top of each other. */
.mario-runner__layer {
  position: absolute;
  inset: 0;
}
.mario-runner__layer svg {
  display: block;
  width: 100%;
  height: 100%;
}

/* Two-frame run cycle: alternate which leg frame is visible via a hard-stepped
   opacity swap (no blending, no skew) so it reads like a two-frame sprite. The
   torso sits on top and never moves. Paused by JS during the jump so the legs
   hold a stride while airborne. */
.mario-runner__legs {
  animation: mario-legs-a var(--mario-legs-ms, 240ms) steps(1, end) infinite;
}
/* At the level end Mario is standing still at the door: freeze the run cycle
   on the first frame so only leg frame A shows and it stops swapping. */
.mario-runner--still .mario-runner__legs {
  animation: none;
}
.mario-runner--still .mario-runner__legs--b {
  opacity: 0;
}
.mario-runner--still .mario-runner__legs--a {
  opacity: 1;
}
.mario-runner__legs--b {
  animation-name: mario-legs-b;
}
@keyframes mario-legs-a {
  0%, 49.9% { opacity: 1; }
  50%, 100% { opacity: 0; }
}
@keyframes mario-legs-b {
  0%, 49.9% { opacity: 0; }
  50%, 100% { opacity: 1; }
}

@media (prefers-reduced-motion: reduce) {
  .idle-stage--footer-below-calendar {
    display: none;
  }
}
`, "",{"version":3,"sources":["webpack://./src/components/idle-animation/mario-level-animation.css"],"names":[],"mappings":"AAAA;;;;;;;;;;;;;;EAcE;;AAEF;EACE,kBAAkB;EAClB,YAAY;EACZ,YAAY;EACZ,gBAAgB;EAChB,oBAAoB;EACpB,UAAU;EACV,yBAAyB;AAC3B;;AAEA;mEACmE;AACnE;;EAEE,WAAW;EACX,YAAY;EACZ,0BAA0B;AAC5B;;AAEA;EACE,cAAc;EACd,WAAW;EACX,YAAY;AACd;;AAEA,+EAA+E;AAC/E;uEACuE;AACvE;EACE,kBAAkB;EAClB,OAAO;EACP,SAAS;EACT,WAAW;EACX,YAAY;EACZ,aAAa;EACb,sBAAsB;EACtB;;;wBAGsB;EACtB,sBAAsB;EACtB,0BAA0B;AAC5B;;AAEA;6EAC6E;AAC7E;EACE,kEAAkE;AACpE;AACA;EACE,KAAK,0BAA0B,EAAE;EACjC,OAAO,4CAA4C,EAAE;AACvD;;AAEA;;;gEAGgE;AAChE;EACE,oEAAoE;AACtE;AACA;EACE,KAAK,4CAA4C,EAAE;EACnD,OAAO,2BAA2B,EAAE;AACtC;;AAEA,6DAA6D;AAC7D;EACE,8CAA8C;AAChD;AACA;EACE,KAAK,UAAU,EAAE;EACjB,OAAO,UAAU,EAAE;AACrB;;AAEA,4EAA4E;AAC5E;;;;;EAKE,kBAAkB;EAClB,SAAS;AACX;;AAEA;;;yDAGyD;AACzD;EACE,WAAW;EACX,YAAY;EACZ,YAAY;EACZ,oEAAoE;AACtE;;AAEA;EACE,WAAW;EACX,YAAY;EACZ,YAAY;EACZ,YAAY;EACZ,oEAAoE;AACtE;;AAEA,yEAAyE;AACzE;EACE,KAAK,wBAAwB,EAAE;EAC/B,OAAO,0BAA0B,EAAE;AACrC;;AAEA;EACE,WAAW;EACX,YAAY;AACd;AACA;EACE,YAAY;AACd;AACA;8DAC8D;;AAE9D,oEAAoE;AACpE;EACE,WAAW;EACX,WAAW;EACX,wBAAwB;EACxB,kBAAkB;EAClB,YAAY;EACZ,0DAA0D;EAC1D,YAAY;AACd;;AAEA,8EAA8E;AAC9E;EACE,aAAa;EACb,QAAQ;EACR,YAAY;AACd;;AAEA;EACE,kBAAkB;EAClB,WAAW;EACX,YAAY;AACd;AACA;EACE,WAAW;EACX,YAAY;AACd;;AAEA;4DAC4D;AAC5D;EACE,+CAA+C;AACjD;AACA;EACE,KAAK,wBAAwB,EAAE;EAC/B,MAAM,2BAA2B,EAAE;EACnC,OAAO,wBAAwB,EAAE;AACnC;;AAEA,kEAAkE;AAClE;EACE,kBAAkB;AACpB;;AAEA,+EAA+E;AAC/E;;uEAEuE;AACvE;EACE,kBAAkB;EAClB,WAAW;EACX,YAAY;EACZ,aAAa;EACb,kDAAkD;AACpD;AACA;EACE,KAAK,uCAAuC,EAAE,aAAa,EAAE;EAC7D;IACE,0GAA0G;IAC1G,aAAa;EACf;EACA;IACE,sEAAsE;IACtE,UAAU;EACZ;AACF;;AAEA,+EAA+E;AAC/E;EACE,WAAW;EACX,YAAY;AACd;AACA;EACE,wDAAwD;AAC1D;AACA;EACE,WAAW,sCAAsC,EAAE;EACnD,MAAM,wCAAwC,EAAE;AAClD;AACA,uEAAuE;AACvE;EACE,WAAW;AACb;AACA;EACE,eAAe;AACjB;;AAEA,+EAA+E;AAC/E;;EAEE,kBAAkB;EAClB,SAAS;AACX;AACA,+CAA+C;AAC/C;EACE,WAAW;EACX,YAAY;AACd;AACA,mDAAmD;AACnD;EACE,YAAY;EACZ,YAAY;AACd;;AAEA,+EAA+E;AAC/E;;;8CAG8C;AAC9C;EACE,kBAAkB;EAClB,SAAS;EACT,SAAS;EACT,WAAW;EACX,YAAY;EACZ,YAAY;EACZ,sBAAsB;EACtB,wBAAwB;AAC1B;AACA;EACE,kBAAkB;EAClB,WAAW;EACX,YAAY;AACd;;AAEA;8DAC8D;AAC9D;EACE,6DAA6D;AAC/D;AACA;EACE,KAAK,wBAAwB,EAAE;EAC/B,MAAM,4BAA4B,EAAE;EACpC,OAAO,wBAAwB,EAAE;AACnC;;AAEA,gFAAgF;AAChF;EACE,6CAA6C;AAC/C;AACA;EACE,KAAK,wBAAwB,EAAE;EAC/B,MAAM,4BAA4B,EAAE;EACpC,OAAO,wBAAwB,EAAE;AACnC;;AAEA,2EAA2E;AAC3E;EACE,kBAAkB;EAClB,QAAQ;AACV;AACA;EACE,cAAc;EACd,WAAW;EACX,YAAY;AACd;;AAEA;;;kCAGkC;AAClC;EACE,0EAA0E;AAC5E;AACA;wEACwE;AACxE;EACE,eAAe;AACjB;AACA;EACE,UAAU;AACZ;AACA;EACE,UAAU;AACZ;AACA;EACE,4BAA4B;AAC9B;AACA;EACE,YAAY,UAAU,EAAE;EACxB,YAAY,UAAU,EAAE;AAC1B;AACA;EACE,YAAY,UAAU,EAAE;EACxB,YAAY,UAAU,EAAE;AAC1B;;AAEA;EACE;IACE,aAAa;EACf;AACF","sourcesContent":["/*\r\n * Mario-style level — decorative idle animation on the footer's top line,\r\n * horizontally clamped (by the director) to the calendar's left/right edges.\r\n *\r\n * A capped runner sprints across a scrolling silhouette \"level\": it leaps a\r\n * pit, head-bumps a row of brick/question blocks (bursting bricks into shards),\r\n * stomps a mushroom-footed walker flat, then runs off the right edge.\r\n *\r\n * Layout mirrors the toy-parade strip: the stage is anchored just above the\r\n * footer's top border (bottom: 100%) and clips its contents. The `left`/`width`\r\n * are set inline by the director to the calendar's box. The world layer scrolls\r\n * via `transform: translateX` while the runner stays put and pumps its legs;\r\n * the two transforms live on separate elements so they never fight. Everything\r\n * is a flat currentColor silhouette.\r\n */\r\n\r\n.idle-stage--footer-below-calendar {\r\n  position: absolute;\r\n  bottom: 100%;\r\n  height: 92px;\r\n  overflow: hidden;\r\n  pointer-events: none;\r\n  z-index: 0;\r\n  color: rgb(120, 145, 175);\r\n}\r\n\r\n/* Shared: opaque fill + one group-level alpha so overlapping silhouette shapes\r\n   flatten to a single solid before transparency (no dark seams). */\r\n.idle-stage--footer-below-calendar .mario-actor__body,\r\n.idle-stage--footer-below-calendar .mario-runner__body {\r\n  width: 100%;\r\n  height: 100%;\r\n  transform-origin: 50% 100%;\r\n}\r\n\r\n.idle-stage--footer-below-calendar svg {\r\n  display: block;\r\n  width: 100%;\r\n  height: 100%;\r\n}\r\n\r\n/* --- scrolling world ------------------------------------------------------ */\r\n/* Carries all the scenery. Starts shifted right and translates fully left so\r\n   the level slides past the fixed runner over the crossing timeline. */\r\n.mario-world {\r\n  position: absolute;\r\n  left: 0;\r\n  bottom: 0;\r\n  width: 100%;\r\n  height: 100%;\r\n  opacity: 0.32;\r\n  will-change: transform;\r\n  /* The freeze offset is the world translate that centers the first brick over\r\n     the fixed runner (runner 30% - brickA center 46% = -16%). The scroll is\r\n     phased by JS-applied classes; before any class is added the world holds at\r\n     its start offset. */\r\n  --mario-freeze-x: -16%;\r\n  transform: translateX(60%);\r\n}\r\n\r\n/* Phase A: scroll in from the start offset until the first brick sits over the\r\n   runner, then hold (fill:both) so the world stays frozen during the jump. */\r\n.mario-world--scroll-in {\r\n  animation: mario-world-in var(--mario-in-ms, 5200ms) ease-out both;\r\n}\r\n@keyframes mario-world-in {\r\n  0% { transform: translateX(60%); }\r\n  100% { transform: translateX(var(--mario-freeze-x)); }\r\n}\r\n\r\n/* Phase B: resume from the frozen offset and scroll the rest of the level past\r\n   (walker stomp + castle finish). Ends at the offset that places the castle's\r\n   door directly over the fixed runner (runner 30% - castle door 108% = -78%),\r\n   so the level halts on the castle rather than scrolling off. */\r\n.mario-world--scroll-rest {\r\n  animation: mario-world-rest var(--mario-rest-ms, 6000ms) linear both;\r\n}\r\n@keyframes mario-world-rest {\r\n  0% { transform: translateX(var(--mario-freeze-x)); }\r\n  100% { transform: translateX(-97%); }\r\n}\r\n\r\n/* Whole-stage fade-out once Mario reaches the castle door. */\r\n.idle-stage--footer-below-calendar.mario-stage--finish {\r\n  animation: mario-stage-fade 900ms ease-in both;\r\n}\r\n@keyframes mario-stage-fade {\r\n  0% { opacity: 1; }\r\n  100% { opacity: 0; }\r\n}\r\n\r\n/* Generic positioned actor inside the world (position set inline as a %). */\r\n.mario-scenery,\r\n.mario-pipe,\r\n.mario-walker,\r\n.mario-pit,\r\n.mario-blocks {\r\n  position: absolute;\r\n  bottom: 0;\r\n}\r\n\r\n/* Background parallax: hills sit on the ground and drift a touch slower via a\r\n   gentle counter-scroll; clouds float higher up. Runs for the whole sequence\r\n   (scroll-in + pause + scroll-rest); JS pauses it during the jump so the\r\n   background truly stops while Mario breaks the brick. */\r\n.mario-scenery--hill {\r\n  width: 90px;\r\n  height: 40px;\r\n  opacity: 0.6;\r\n  animation: mario-parallax var(--mario-total-ms, 12000ms) linear both;\r\n}\r\n\r\n.mario-scenery--cloud {\r\n  width: 60px;\r\n  height: 26px;\r\n  bottom: 56px;\r\n  opacity: 0.5;\r\n  animation: mario-parallax var(--mario-total-ms, 12000ms) linear both;\r\n}\r\n\r\n/* Slight counter-drift so background layers lag the foreground scroll. */\r\n@keyframes mario-parallax {\r\n  0% { transform: translateX(0); }\r\n  100% { transform: translateX(28%); }\r\n}\r\n\r\n.mario-pipe {\r\n  width: 30px;\r\n  height: 36px;\r\n}\r\n.mario-pipe--tall {\r\n  height: 48px;\r\n}\r\n/* The pipe SVG uses preserveAspectRatio=\"none\" so it stretches to fill its box\r\n   at any height, keeping the base flush on the ground line. */\r\n\r\n/* The pit: a dark notch in the ground line the runner jumps over. */\r\n.mario-pit {\r\n  width: 46px;\r\n  height: 8px;\r\n  background: currentColor;\r\n  border-radius: 2px;\r\n  opacity: 0.5;\r\n  /* Render as a \"gap\": a shadowed dip below the baseline. */\r\n  bottom: -4px;\r\n}\r\n\r\n/* Floating block row, raised so the runner's jump apex bumps its underside. */\r\n.mario-blocks {\r\n  display: flex;\r\n  gap: 3px;\r\n  bottom: 48px;\r\n}\r\n\r\n.mario-block {\r\n  position: relative;\r\n  width: 22px;\r\n  height: 22px;\r\n}\r\n.mario-block .mario-actor__body {\r\n  width: 100%;\r\n  height: 100%;\r\n}\r\n\r\n/* One-shot bounce triggered by JS on the brick actually hit (the first brick),\r\n   at the moment of the jump apex. Runs once then settles. */\r\n.mario-block--bumped {\r\n  animation: mario-block-bump 220ms ease-out both;\r\n}\r\n@keyframes mario-block-bump {\r\n  0% { transform: translateY(0); }\r\n  45% { transform: translateY(-6px); }\r\n  100% { transform: translateY(0); }\r\n}\r\n\r\n/* A broken brick vanishes (its shards carry the visible burst). */\r\n.mario-block--broken {\r\n  visibility: hidden;\r\n}\r\n\r\n/* --- brick shards --------------------------------------------------------- */\r\n/* The world layer is already ~0.32 opacity, so shards need to be near-opaque\r\n   (and a bit larger) to read against it during the quick burst. They launch up\r\n   and outward like an explosion, then arc back down past the origin. */\r\n.mario-shard {\r\n  position: absolute;\r\n  width: 14px;\r\n  height: 14px;\r\n  opacity: 0.95;\r\n  animation: mario-shard-fly 680ms ease-out forwards;\r\n}\r\n@keyframes mario-shard-fly {\r\n  0% { transform: translate(0, 0) rotate(0deg); opacity: 0.95; }\r\n  40% {\r\n    transform: translate(calc(var(--mario-shard-dx, 16px) * 0.6), var(--mario-shard-dy, -28px)) rotate(140deg);\r\n    opacity: 0.95;\r\n  }\r\n  100% {\r\n    transform: translate(var(--mario-shard-dx, 16px), 30px) rotate(300deg);\r\n    opacity: 0;\r\n  }\r\n}\r\n\r\n/* --- stompable walker ----------------------------------------------------- */\r\n.mario-walker {\r\n  width: 26px;\r\n  height: 22px;\r\n}\r\n.mario-walker .mario-actor__body {\r\n  animation: mario-walker-toddle 0.5s ease-in-out infinite;\r\n}\r\n@keyframes mario-walker-toddle {\r\n  0%, 100% { transform: translateY(0) rotate(-2deg); }\r\n  50% { transform: translateY(-1px) rotate(2deg); }\r\n}\r\n/* Flattened after the stomp: squash to the ground and stop toddling. */\r\n.mario-walker--stomped {\r\n  height: 8px;\r\n}\r\n.mario-walker--stomped .mario-actor__body {\r\n  animation: none;\r\n}\r\n\r\n/* --- level-end flagpole + castle ------------------------------------------ */\r\n.mario-flag,\r\n.mario-castle {\r\n  position: absolute;\r\n  bottom: 0;\r\n}\r\n/* Tall flagpole with a pennant near the top. */\r\n.mario-flag {\r\n  width: 24px;\r\n  height: 90px;\r\n}\r\n/* The castle silhouette Mario ends the level at. */\r\n.mario-castle {\r\n  width: 116px;\r\n  height: 80px;\r\n}\r\n\r\n/* --- the runner ----------------------------------------------------------- */\r\n/* Fixed at 30% of the stage the whole time; the world scrolls beneath it. The\r\n   vertical jump and stomp-dip are standalone one-shot animations applied by JS\r\n   at the right phase, so they never fight the (now phased) world scroll. The\r\n   leg-frame swap lives on the inner layers. */\r\n.mario-runner {\r\n  position: absolute;\r\n  bottom: 0;\r\n  left: 30%;\r\n  width: 30px;\r\n  height: 40px;\r\n  opacity: 0.4;\r\n  will-change: transform;\r\n  transform: translateY(0);\r\n}\r\n.mario-runner__body {\r\n  position: relative;\r\n  width: 100%;\r\n  height: 100%;\r\n}\r\n\r\n/* Standalone straight-up jump (no horizontal motion) run once during the pause\r\n   so Mario breaks the brick directly above him, then lands. */\r\n.mario-runner--jump {\r\n  animation: mario-jump 620ms cubic-bezier(0.3, 0, 0.4, 1) both;\r\n}\r\n@keyframes mario-jump {\r\n  0% { transform: translateY(0); }\r\n  50% { transform: translateY(-30px); }\r\n  100% { transform: translateY(0); }\r\n}\r\n\r\n/* Small stomp dip run once when the walker aligns, during the resumed scroll. */\r\n.mario-runner--stomp {\r\n  animation: mario-stomp 300ms ease-in-out both;\r\n}\r\n@keyframes mario-stomp {\r\n  0% { transform: translateY(0); }\r\n  50% { transform: translateY(-18px); }\r\n  100% { transform: translateY(0); }\r\n}\r\n\r\n/* The static torso and both leg frames are stacked on top of each other. */\r\n.mario-runner__layer {\r\n  position: absolute;\r\n  inset: 0;\r\n}\r\n.mario-runner__layer svg {\r\n  display: block;\r\n  width: 100%;\r\n  height: 100%;\r\n}\r\n\r\n/* Two-frame run cycle: alternate which leg frame is visible via a hard-stepped\r\n   opacity swap (no blending, no skew) so it reads like a two-frame sprite. The\r\n   torso sits on top and never moves. Paused by JS during the jump so the legs\r\n   hold a stride while airborne. */\r\n.mario-runner__legs {\r\n  animation: mario-legs-a var(--mario-legs-ms, 240ms) steps(1, end) infinite;\r\n}\r\n/* At the level end Mario is standing still at the door: freeze the run cycle\r\n   on the first frame so only leg frame A shows and it stops swapping. */\r\n.mario-runner--still .mario-runner__legs {\r\n  animation: none;\r\n}\r\n.mario-runner--still .mario-runner__legs--b {\r\n  opacity: 0;\r\n}\r\n.mario-runner--still .mario-runner__legs--a {\r\n  opacity: 1;\r\n}\r\n.mario-runner__legs--b {\r\n  animation-name: mario-legs-b;\r\n}\r\n@keyframes mario-legs-a {\r\n  0%, 49.9% { opacity: 1; }\r\n  50%, 100% { opacity: 0; }\r\n}\r\n@keyframes mario-legs-b {\r\n  0%, 49.9% { opacity: 0; }\r\n  50%, 100% { opacity: 1; }\r\n}\r\n\r\n@media (prefers-reduced-motion: reduce) {\r\n  .idle-stage--footer-below-calendar {\r\n    display: none;\r\n  }\r\n}\r\n"],"sourceRoot":""}]);
// Exports
export default ___CSS_LOADER_EXPORT___;
